import React from 'react';
import { Platform } from 'react-native';
import { Button } from '@/components/gluestack/button';
import { HugeIconsWrapper } from '@/components/ui/HugeIconsWrapper';
import { ButtonText } from '@/components/gluestack/button';
import type { IconSvgElement } from '@hugeicons/react-native';

// Define size mapping for icon sizes
const iconSizeMap = {
  xs: 16,
  sm: 17,
  md: 19,
  lg: 21,
  xl: 23
};

/** Styling for icon buttons placed in the app stack header (bar uses bg-secondary-500 on small / primary on md+). */
const APP_HEADER_BAR_CLASS =
  'bg-secondary-600 dark:bg-secondary-400 md:bg-primary-500';

interface IButtonWrapperProps {
  icon?: IconSvgElement;
  text?: string;
  variant?: 'solid' | 'outline' | 'link';
  action?: 'primary' | 'secondary' | 'positive' | 'negative' | 'neutral' | 'default';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  isDisabled?: boolean;
  onPress?: () => void;
  /**
   * Fires on touch-down rather than touch-up. Use sparingly — primarily as an
   * iOS workaround when a sibling multiline `TextInput` is focused: iOS's
   * outside-tap recognizer resigns first responder on the first tap, and the
   * resulting `KeyboardAvoidingView` layout shift can cancel the press before
   * `onPress` fires (see `BottomTextInput`).
   */
  onPressIn?: () => void;
  className?: string;
  iconSize?: number;
  iconClassName?: string;
  hitSlop?: { top: number; bottom: number; left: number; right: number };
  enhanceTouchArea?: boolean;
  rightSlot?: React.ReactNode;
  // On web, prevent button from stealing focus on mousedown (keeps input focused)
  preventFocusOnMouseDown?: boolean;
  accessibilityLabel?: string;
  /**
   * Set when the control sits in the top `ScreenHeader` / stack header. Applies
   * `action="secondary"` and a background that matches the header strip
   * (`bg-secondary-500` area). Use with `!isLargeScreen` where the large header
   * is rendered by `LargeScreenTitleBar` instead.
   */
  inAppHeaderBar?: boolean;
}

const ButtonWrapperBase = React.forwardRef<
  React.ComponentRef<typeof Button>,
  IButtonWrapperProps
>(({
  icon,
  text,
  variant = 'solid',
  action = 'primary',
  size = 'md',
  className = '',
  iconClassName = '',
  iconSize,
  isDisabled,
  onPress,
  onPressIn,
  hitSlop,
  enhanceTouchArea = text ? false : true,
  rightSlot,
  preventFocusOnMouseDown,
  inAppHeaderBar = false,
  ...props
}, ref) => {
  const effectiveAction = inAppHeaderBar ? 'secondary' : action;
  const mergedClassName = inAppHeaderBar
    ? [APP_HEADER_BAR_CLASS, className].filter(Boolean).join(' ')
    : className;

  // Determine icon size based on button size or use provided size
  const iconDimension = iconSize ?? iconSizeMap[size as keyof typeof iconSizeMap] ?? 19;
  
  // Only apply aspect-square and p-0 if it's an icon-only button
  const isIconOnly = Boolean(icon && !text);
  
  // Add minimum dimensions for touch targets and preserve any existing classes
  const buttonStyle = isIconOnly ? `aspect-square p-0 ${mergedClassName}` : mergedClassName;
  
  // Only apply automatic icon color if iconClassName doesn't already have a color class
  const hasCustomColor = iconClassName.includes('color');
  let iconColor = '';
  
  if (!hasCustomColor) {
    // Special handling for neutral action
    if (effectiveAction === 'neutral') {
      if (variant === 'solid') {
        iconColor = 'color-neutral-600 dark:color-neutral-400';
      } else {
        iconColor = 'color-neutral-500 dark:color-neutral-500';
      }
    } else {
      iconColor = variant === 'solid' ? 'color-white' : 'color-primary-500';
    }
  }

  // Default hitSlop to increase touch area if not provided and enhanceTouchArea is true
  const defaultHitSlop = enhanceTouchArea && !hitSlop ? { top: 5, bottom: 5, left: 5, right: 5 } : hitSlop;
  
  const webMouseDownProps = Platform.OS === 'web' && preventFocusOnMouseDown
    ? { onMouseDown: (e: any) => { if (e && e.preventDefault) e.preventDefault(); }, focusable: false as const, tabIndex: -1 as const }
    : {};
  
  return (
    <Button
      ref={ref}
      size={size}
      variant={variant}
      action={effectiveAction}
      className={buttonStyle}
      isDisabled={isDisabled}
      onPress={onPress}
      onPressIn={onPressIn}
      hitSlop={defaultHitSlop}
      {...webMouseDownProps}
      {...props}
    >
      {icon && (
        <HugeIconsWrapper 
          icon={icon} 
          size={iconDimension}
          className={`shrink-0 ${iconColor} ${iconClassName}`}
        />
      )}
      {text && <ButtonText numberOfLines={1} className="shrink">{text}</ButtonText>}
      {rightSlot}
    </Button>
  );
});

ButtonWrapperBase.displayName = 'ButtonWrapperBase';

// Memoize the component to prevent unnecessary re-renders
const ButtonWrapper = React.memo(ButtonWrapperBase);

ButtonWrapper.displayName = 'ButtonWrapper';

export { ButtonWrapper };